<script lang="ts">
  /**
   * An on/off switch: a pill track and a knob that slides. `role="switch"`,
   * named by `label`, so a screen reader hears what it turns on and whether
   * it is on. TwinTen's settings switch, measure for measure.
   *
   * The parent owns `checked`. A press reports the value it asks for
   * (`onchange(!checked)`); the switch never flips itself.
   *
   * The house look has no checkbox: a thing that is on or off is a switch.
   *
   * Test id: `testid`, on the button. No default (a switch is usually one of
   * several on a page; give each its own).
   */
  interface Props {
    /** The accessible name: what the switch turns on. */
    readonly label: string;
    readonly checked: boolean;
    /** Called with the value a press asks for (the opposite of `checked`). */
    readonly onchange: (checked: boolean) => void;
    readonly disabled?: boolean;
    /** The id of a line that describes it (a row's detail), for `aria-describedby`. */
    readonly describedby?: string | undefined;
    readonly testid?: string | undefined;
  }
  const { label, checked, onchange, disabled = false, describedby, testid }: Props = $props();
</script>

<button
  type="button"
  class="ui-switch"
  class:ui-switch--on={checked}
  role="switch"
  aria-checked={checked}
  aria-label={label}
  aria-describedby={describedby}
  {disabled}
  data-testid={testid}
  onclick={() => onchange(!checked)}
>
  <span class="ui-switch-knob"></span>
</button>

<style>
  .ui-switch {
    box-sizing: border-box;
    flex: 0 0 auto;
    position: relative;
    width: 2.6rem;
    height: 1.5rem;
    border: 0;
    padding: 0;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-muted);
    transition: background 0.15s ease;
  }

  /* The track is 1.5rem tall; the target is the full 2.75rem. */
  .ui-switch::before {
    content: '';
    position: absolute;
    inset: -0.625rem -0.1rem;
  }

  .ui-switch:disabled {
    opacity: 0.6;
  }

  .ui-switch--on {
    background: var(--ui-accent);
  }

  .ui-switch:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  /* Near-white in both appearances (--ui-on-deep): the ink on the accent is
     dark in the dark appearance and would vanish on the off track. The
     shadow is the knob's edge where its fill and a light track run close. */
  .ui-switch-knob {
    position: absolute;
    top: 0.15rem;
    left: 0.15rem;
    width: 1.2rem;
    height: 1.2rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-on-deep);
    /* house-check-allow: the knob's drawn edge, not an elevation. */
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.35);
    transition: transform 0.15s ease;
  }

  .ui-switch--on .ui-switch-knob {
    transform: translateX(1.1rem);
  }

  @media (prefers-reduced-motion: reduce) {
    .ui-switch,
    .ui-switch-knob {
      transition: none;
    }
  }
</style>
